import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../hooks/useLanguage'
import { useAuth } from '../hooks/useAuth'
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar'
import { 
  BarChartIcon,
  FileIcon,
  UsersIcon,
  MapIcon,
  SettingsIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon,
  MenuIcon,
  CloseIcon
} from '../components/icons/Icons'

const OfficialDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { user, signOut } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const stats = [
    { title: 'Assigned Issues', count: 45, color: 'text-blue-600', bg: 'bg-blue-100', icon: FileIcon },
    { title: 'Pending Review', count: 12, color: 'text-yellow-600', bg: 'bg-yellow-100', icon: ClockIcon },
    { title: 'In Progress', count: 18, color: 'text-orange-600', bg: 'bg-orange-100', icon: AlertTriangleIcon },
    { title: 'Resolved', count: 15, color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircleIcon }
  ]

  const assignedIssues = [
    {
      id: 'CIV-001',
      title: 'Pothole on Main Street',
      citizen: 'John Doe',
      category: 'Roads',
      status: 'In Progress',
      date: '2024-01-15',
      priority: 'High',
      location: 'Main Street, Sector 5'
    },
    {
      id: 'CIV-002',
      title: 'Street Light Not Working',
      citizen: 'Jane Smith',
      category: 'Electricity',
      status: 'Pending',
      date: '2024-01-14',
      priority: 'Medium',
      location: 'Park Avenue, Block B'
    },
    {
      id: 'CIV-003',
      title: 'Water Leak in Pipeline',
      citizen: 'Mike Johnson',
      category: 'Water Supply',
      status: 'Under Review',
      date: '2024-01-13',
      priority: 'High',
      location: 'Garden Road, Phase 2'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Resolved': return 'text-green-600 bg-green-100'
      case 'In Progress': return 'text-orange-600 bg-orange-100'
      case 'Pending': return 'text-yellow-600 bg-yellow-100'
      case 'Under Review': return 'text-blue-600 bg-blue-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100'
      case 'Medium': return 'text-yellow-600 bg-yellow-100'
      case 'Low': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const handleSignOut = async () => {
    const { error } = await signOut()
    if (!error) {
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {sidebarOpen ? <CloseIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
              </button>
              <div className="flex items-center space-x-3">
                <img src="/logo.png" alt="Logo" className="w-8 h-8" />
                <h1 className="text-xl font-semibold text-gray-900">{t('auth.officialDashboard')}</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.email}</p>
                <p className="text-xs text-gray-500">Official</p>
              </div>
              <button
                onClick={handleSignOut}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="official"
          currentPage="admin-dashboard"
        />

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-5">
            {/* Welcome Section */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome, {user?.full_name || 'Official'}!</h2>
              <p className="text-gray-600 text-sm">Managing civic issues for your department</p>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/admin/issues')}
                className="bg-green-600 text-white p-4 sm:p-5 rounded-lg hover:bg-green-700 transition-colors text-left"
              >
                <FileIcon className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3" />
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">Review Issues</h3>
                <p className="text-green-100 text-xs sm:text-sm">Process and update assigned issues</p>
              </button>
              
              <button
                onClick={() => navigate('/analytics')}
                className="bg-blue-600 text-white p-4 sm:p-5 rounded-lg hover:bg-blue-700 transition-colors text-left"
              >
                <BarChartIcon className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3" />
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">View Analytics</h3>
                <p className="text-blue-100 text-xs sm:text-sm">Monitor department performance</p>
              </button>
              
              <button
                onClick={() => navigate('/admin/citizens')}
                className="bg-purple-600 text-white p-4 sm:p-5 rounded-lg hover:bg-purple-700 transition-colors text-left"
              >
                <UsersIcon className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3" />
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">Manage Citizens</h3>
                <p className="text-purple-100 text-xs sm:text-sm">View citizen profiles and reports</p>
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                  <div className="flex items-center">
                    <div className={`${stat.bg} p-2 sm:p-3 rounded-lg`}>
                      <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.color}`} />
                    </div>
                    <div className="ml-3 sm:ml-4">
                      <p className="text-xs sm:text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">{stat.count}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Assigned Issues */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <h3 className="text-lg font-semibold text-gray-900">Recent Assigned Issues</h3>
                <button 
                  onClick={() => navigate('/admin/issues')}
                  className="text-green-600 hover:text-green-700 text-sm font-medium self-start sm:self-auto"
                >
                  View All
                </button>
              </div>
              
              {/* Mobile view for assigned issues */}
              <div className="block sm:hidden space-y-4">
                {assignedIssues.map((issue) => (
                  <div key={issue.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{issue.title}</h3>
                        <p className="text-xs text-gray-500">#{issue.id}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                        {issue.status}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Citizen:</span>
                        <span className="truncate">{issue.citizen}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Category:</span>
                        <span>{issue.category}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Location:</span>
                        <span className="truncate">{issue.location}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Date:</span>
                        <span>{issue.date}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-xs mr-2">Priority:</span>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(issue.priority)}`}>
                          {issue.priority}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between pt-3 border-t border-gray-100">
                      <button 
                        onClick={() => navigate(`/admin/issues`)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View Details
                      </button>
                      <button className="text-green-600 hover:text-green-700 text-sm font-medium">
                        Update
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop view for assigned issues */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Citizen</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assignedIssues.map((issue) => (
                      <tr key={issue.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{issue.title}</div>
                            <div className="text-xs text-gray-500">{issue.id}</div>
                            <div className="text-xs text-gray-400">{issue.location}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{issue.citizen}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{issue.category}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(issue.status)}`}>
                            {issue.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getPriorityColor(issue.priority)}`}>
                            {issue.priority}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{issue.date}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm space-y-2">
                          <div className="flex flex-wrap gap-2">
                            <button 
                              onClick={() => navigate(`/admin/issues`)}
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              View
                            </button>
                            <button className="text-green-600 hover:text-green-700 text-sm">Update</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Department Performance */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Resolution Rate</span>
                    <span className="text-sm font-medium text-green-600">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Avg. Response Time</span>
                    <span className="text-sm font-medium text-blue-600">2.3 days</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '70%' }}></div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Citizen Satisfaction</span>
                    <span className="text-sm font-medium text-purple-600">4.2/5</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-purple-600 h-2.5 rounded-full" style={{ width: '84%' }}></div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Issues This Month</span>
                    <span className="text-lg font-semibold text-gray-900">127</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Resolved This Week</span>
                    <span className="text-lg font-semibold text-green-600">23</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Pending Actions</span>
                    <span className="text-lg font-semibold text-yellow-600">12</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Active Citizens</span>
                    <span className="text-lg font-semibold text-blue-600">1,456</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default OfficialDashboard